import { format, formatDistanceToNow, isToday, isTomorrow, isYesterday, isPast, differenceInDays } from 'date-fns';
import { it } from 'date-fns/locale';
import { normalizeStatus, TASK_STATUS } from '@/constants/taskStatuses';

// Helper to normalize Date or timestamp to Date
function toDate(date: Date | number | null | undefined): Date | null {
  if (!date) return null;
  if (date instanceof Date) return date;
  if (typeof date === 'number') return new Date(date);
  return null;
}

export function formatDate(date: Date | number | null | undefined): string {
  const d = toDate(date);
  if (!d) return '—';

  if (isToday(d)) return 'Oggi';
  if (isTomorrow(d)) return 'Domani';
  if (isYesterday(d)) return 'Ieri';

  const diff = differenceInDays(d, new Date());
  if (diff > 0 && diff <= 7) {
  return format(d, 'EEEE', { locale: it });
  }

  return format(d, 'd MMM', { locale: it });
}

export function formatDateFull(date: Date | number | null | undefined): string {
  const d = toDate(date);
  if (!d) return '—';
  return format(d, 'd MMMM yyyy', { locale: it });
}

export function formatTimeAgo(date: Date | number | null | undefined): string {
  const d = toDate(date);
  if (!d) return '—';
  return formatDistanceToNow(d, { addSuffix: true, locale: it });
}

export function formatTime(date: Date | number | null | undefined): string {
  const d = toDate(date);
  if (!d) return '—';
  return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

// Re-export from duration.ts for backward compatibility
export { formatDuration } from './duration';

export function isOverdue(date: Date | number | null | undefined): boolean {
  const d = toDate(date);
  if (!d) return false;
  return isPast(d) && !isToday(d);
}

export function getStatusColor(status: string): string {
  const normalized = normalizeStatus(status);

  switch (normalized) {
  case TASK_STATUS.COMPLETED:
  case TASK_STATUS.CANCELLED:
      return 'text-accent-emerald';
  case TASK_STATUS.OPEN:
  case TASK_STATUS.TODO:
      return 'text-text-secondary';
  case TASK_STATUS.IN_PROGRESS:
      return 'text-accent-blue';
  case TASK_STATUS.BLOCKED:
      return 'text-accent-rose';
  default:
      return 'text-text-tertiary';
  }
}

export function getStatusBgColor(status: string): string {
  const normalized = normalizeStatus(status);

  switch (normalized) {
  case TASK_STATUS.COMPLETED:
  case TASK_STATUS.CANCELLED:
      return 'bg-accent-emerald/15 text-accent-emerald';
  case TASK_STATUS.OPEN:
  case TASK_STATUS.TODO:
      return 'bg-surface-tertiary text-text-secondary';
  case TASK_STATUS.IN_PROGRESS:
      return 'bg-accent-blue/15 text-accent-blue';
  case TASK_STATUS.BLOCKED:
      return 'bg-accent-rose/15 text-accent-rose';
  default:
      return 'bg-surface-tertiary text-text-tertiary';
  }
}

export function getPriorityColor(priority: string | null): string {
  if (!priority) return 'text-text-tertiary';

  const normalized = priority.toLowerCase();

  if (normalized.includes('urgent') || normalized.includes('high')) {
  return 'text-accent-rose';
  }
  if (normalized.includes('normal') || normalized.includes('medium')) {
  return 'text-accent-amber';
  }
  if (normalized.includes('low')) {
  return 'text-accent-cyan';
  }
  return 'text-text-tertiary';
}

export function getInitials(name: string): string {
  // If the name looks like a UUID, return empty (profiles not yet loaded)
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-/.test(name)) return '';
  return name
  .split(' ')
  .map(word => word[0])
  .join('')
  .toUpperCase()
  .slice(0, 2);
}

export function getAvatarColor(name: string): string {
  const colors = [
  'bg-accent-violet',
  'bg-accent-blue',
  'bg-accent-emerald',
  'bg-accent-amber',
  'bg-accent-rose',
  'bg-accent-cyan',
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
  hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
}

